import { useEffect, useMemo, useState } from "react";
import { Link, useParams } from "react-router-dom";
import Breadcrumb from "@/components/feature/Breadcrumb";
import ArticleCard from "@/components/feature/ArticleCard";
import { usePageMeta } from "@/hooks/usePageMeta";
import { formatDate, getArticleById, getCategoryById, getRelatedArticles } from "@/utils/data";
import { isFavorite, markAsRead, toggleFavorite } from "@/utils/storage";

export default function Detail() {
  const { id = "" } = useParams();
  const article = getArticleById(id);
  const category = article ? getCategoryById(article.categoryId) : undefined;

  const [favorited, setFavorited] = useState(false);
  const [toast, setToast] = useState("");

  usePageMeta(
    article ? `${article.title} - 自慰健康生活` : "文章未找到 - 自慰健康生活",
    article ? article.summary.slice(0, 120) : "文章未找到或已删除。",
    article ? `${article.title},自慰,健康生活,${category ? category.name : ""}` : "自慰,健康生活",
  );

  useEffect(() => {
    if (!article) return;
    setFavorited(isFavorite(article.id));
    markAsRead(article.id);
    window.scrollTo({ top: 0 });
  }, [article]);

  useEffect(() => {
    if (!toast) return;
    const timer = setTimeout(() => setToast(""), 2200);
    return () => clearTimeout(timer);
  }, [toast]);

  const related = useMemo(() => (article ? getRelatedArticles(article, 4) : []), [article]);

  const readingMinutes = useMemo(() => {
    if (!article) return 1;
    const chars = article.sections.reduce(
      (sum, section) => sum + section.heading.length + section.paragraphs.join("").length,
      0,
    );
    return Math.max(1, Math.round(chars / 400));
  }, [article]);

  const handleCopy = async (message: string) => {
    try {
      await navigator.clipboard.writeText(window.location.href);
      setToast(message);
    } catch {
      setToast("复制失败，请手动复制地址栏链接");
    }
  };

  const handleFavorite = () => {
    if (!article) return;
    const next = toggleFavorite(article.id);
    setFavorited(next.includes(article.id));
    setToast(next.includes(article.id) ? "已加入收藏（保存在本地浏览器）" : "已取消收藏");
  };

  const scrollToSection = (index: number) => {
    const element = document.getElementById(`section-${index}`);
    if (element) element.scrollIntoView({ behavior: "smooth", block: "start" });
  };

  if (!article) {
    return (
      <div className="w-full max-w-[1200px] mx-auto px-4 md:px-8 py-20 text-center">
        <i className="ri-file-unknow-line text-5xl text-foreground-300"></i>
        <h1 className="mt-5 font-heading text-2xl font-bold text-foreground-900">文章未找到或已删除</h1>
        <p className="mt-3 text-sm text-foreground-600">你访问的内容可能已被移除或链接有误。</p>
        <Link
          to="/"
          className="mt-6 inline-flex items-center gap-2 px-5 py-2.5 rounded-md bg-primary-500 text-background-50 text-sm hover:bg-primary-600 transition-colors"
        >
          <i className="ri-arrow-left-line"></i>
          返回首页
        </Link>
      </div>
    );
  }

  return (
    <div className="w-full">
      <div className="w-full max-w-[1200px] mx-auto px-4 md:px-8 pt-6 md:pt-8">
        <Breadcrumb
          items={[
            { label: category?.name ?? "资讯", to: `/category/${article.categoryId}` },
            { label: article.title },
          ]}
        />
      </div>

      <div className="w-full max-w-[1200px] mx-auto px-4 md:px-8 py-6 md:py-8">
        <div className="grid grid-cols-1 lg:grid-cols-4 gap-8 lg:gap-10">
          {/* 正文 */}
          <article className="lg:col-span-3">
            <div className="flex flex-wrap items-center gap-2 mb-4">
              {category && (
                <Link
                  to={`/category/${category.id}`}
                  className="px-3 py-1 rounded-full text-xs bg-primary-50 text-primary-700 hover:bg-primary-100 transition-colors"
                >
                  {category.name}
                </Link>
              )}
              {article.tags.map((tag) => (
                <span key={tag} className="px-3 py-1 rounded-full text-xs bg-secondary-100 text-secondary-900">
                  {tag}
                </span>
              ))}
            </div>

            <h1 className="font-heading text-2xl md:text-4xl font-bold text-foreground-950 leading-snug">
              {article.title}
            </h1>

            <div className="mt-5 flex flex-wrap items-center gap-x-5 gap-y-2 text-sm text-foreground-500">
              <span className="flex items-center gap-1.5">
                <i className="ri-user-line"></i>
                {article.author}
              </span>
              <span className="flex items-center gap-1.5">
                <i className="ri-calendar-line"></i>
                {formatDate(article.date)}
              </span>
              <span className="flex items-center gap-1.5">
                <i className="ri-eye-line"></i>
                {article.readCount.toLocaleString()} 次阅读
              </span>
              <span className="flex items-center gap-1.5">
                <i className="ri-time-line"></i>
                约 {readingMinutes} 分钟
              </span>
            </div>

            <div className="mt-6 rounded-lg overflow-hidden bg-background-100">
              <img
                src={article.cover}
                alt={article.title}
                title={`${article.title} 自慰健康生活`}
                className="w-full h-56 md:h-80 object-cover object-top"
              />
            </div>

            {/* 移动端目录 */}
            <details className="lg:hidden mt-6 rounded-lg bg-background-100 border border-background-200 p-4">
              <summary className="text-sm font-semibold text-foreground-900 cursor-pointer flex items-center gap-2">
                <i className="ri-list-check-2 text-primary-600"></i>
                本文目录
              </summary>
              <ol className="mt-3 space-y-2">
                {article.sections.map((section, index) => (
                  <li key={section.heading}>
                    <button
                      type="button"
                      onClick={() => scrollToSection(index)}
                      className="text-left text-sm text-foreground-700 hover:text-primary-700 cursor-pointer"
                    >
                      {index + 1}. {section.heading}
                    </button>
                  </li>
                ))}
              </ol>
            </details>

            <div className="mt-8 space-y-8">
              {article.sections.map((section, index) => (
                <section key={section.heading} id={`section-${index}`} className="scroll-mt-24">
                  <h2 className="font-heading text-xl md:text-2xl font-bold text-foreground-950 flex items-start gap-3">
                    <span className="shrink-0 w-7 h-7 flex items-center justify-center rounded-md bg-accent-100 text-accent-700 text-sm font-bold">
                      {index + 1}
                    </span>
                    {section.heading}
                  </h2>
                  <div className="mt-4 space-y-4">
                    {section.paragraphs.map((paragraph, pIndex) => (
                      <p key={pIndex} className="text-base text-foreground-800 leading-loose">
                        {paragraph}
                      </p>
                    ))}
                  </div>
                </section>
              ))}
            </div>

            {/* 分享与收藏 */}
            <div className="mt-10 pt-6 border-t border-background-200 flex flex-wrap items-center justify-between gap-4">
              <div className="flex items-center gap-2">
                <button
                  type="button"
                  onClick={handleFavorite}
                  className={`inline-flex items-center gap-2 px-4 py-2 rounded-md text-sm transition-colors cursor-pointer whitespace-nowrap ${
                    favorited
                      ? "bg-accent-500 text-background-50"
                      : "bg-background-100 text-foreground-800 border border-background-200 hover:bg-background-200"
                  }`}
                >
                  <i className={favorited ? "ri-bookmark-fill" : "ri-bookmark-line"}></i>
                  {favorited ? "已收藏" : "收藏文章"}
                </button>
              </div>
              <div className="flex items-center gap-2">
                <span className="text-sm text-foreground-500 mr-1">分享：</span>
                <button
                  type="button"
                  aria-label="复制链接"
                  onClick={() => handleCopy("链接已复制到剪贴板")}
                  className="w-9 h-9 flex items-center justify-center rounded-full bg-background-100 border border-background-200 text-foreground-700 hover:text-primary-700 hover:bg-background-200 transition-colors cursor-pointer"
                >
                  <i className="ri-link"></i>
                </button>
                <button
                  type="button"
                  aria-label="分享到微博"
                  onClick={() => handleCopy("链接已复制，可前往微博粘贴分享")}
                  className="w-9 h-9 flex items-center justify-center rounded-full bg-background-100 border border-background-200 text-foreground-700 hover:text-primary-700 hover:bg-background-200 transition-colors cursor-pointer"
                >
                  <i className="ri-weibo-fill"></i>
                </button>
                <button
                  type="button"
                  aria-label="分享给微信好友"
                  onClick={() => handleCopy("链接已复制，可前往微信粘贴分享")}
                  className="w-9 h-9 flex items-center justify-center rounded-full bg-background-100 border border-background-200 text-foreground-700 hover:text-primary-700 hover:bg-background-200 transition-colors cursor-pointer"
                >
                  <i className="ri-wechat-fill"></i>
                </button>
                <button
                  type="button"
                  aria-label="分享到QQ"
                  onClick={() => handleCopy("链接已复制，可前往QQ粘贴分享")}
                  className="w-9 h-9 flex items-center justify-center rounded-full bg-background-100 border border-background-200 text-foreground-700 hover:text-primary-700 hover:bg-background-200 transition-colors cursor-pointer"
                >
                  <i className="ri-qq-fill"></i>
                </button>
              </div>
            </div>

            <p className="mt-6 text-xs text-foreground-500 leading-relaxed">
              温馨提示：本文为健康科普内容，仅供参考，不能替代专业医疗建议。如有具体健康问题，请咨询专业医生。收藏记录保存在你的浏览器本地，不会上传服务器。
            </p>
          </article>

          {/* 侧栏目录 */}
          <aside className="hidden lg:block lg:col-span-1">
            <div className="sticky top-24">
              <div className="rounded-lg bg-background-100 border border-background-200 p-5">
                <h2 className="flex items-center gap-2 text-sm font-semibold text-foreground-900 mb-3">
                  <i className="ri-list-check-2 text-primary-600"></i>
                  本文目录
                </h2>
                <ol className="space-y-2">
                  {article.sections.map((section, index) => (
                    <li key={section.heading}>
                      <button
                        type="button"
                        onClick={() => scrollToSection(index)}
                        className="text-left text-sm text-foreground-700 hover:text-primary-700 transition-colors cursor-pointer line-clamp-2"
                      >
                        <span className="text-primary-500 mr-1">{String(index + 1).padStart(2, "0")}</span>
                        {section.heading}
                      </button>
                    </li>
                  ))}
                </ol>
              </div>
            </div>
          </aside>
        </div>

        {/* 相关推荐 */}
        <section className="mt-12 md:mt-16">
          <div className="flex items-center gap-2 mb-6">
            <i className="ri-links-line text-primary-600 text-xl"></i>
            <h2 className="font-heading text-xl md:text-2xl font-bold text-foreground-950">相关推荐</h2>
          </div>
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4 md:gap-5">
            {related.map((item) => (
              <ArticleCard key={item.id} article={item} />
            ))}
          </div>
        </section>
      </div>

      {toast && (
        <div className="fixed bottom-24 left-1/2 -translate-x-1/2 z-50 px-5 py-3 rounded-md bg-foreground-950 text-background-50 text-sm animate-fade-in">
          {toast}
        </div>
      )}
    </div>
  );
}